import React from 'react';
import styled from 'styled-components';
import { ProgressIndicator, Typography } from '@agilepoint/wat-react-ux';
import { IMobileBuild, useGetBuildRequestByStatusOrBuildIdQuery, useOptionalDownloadMobilePackageQuery } from 'modules/my-apps/queries';
import { saveFile, toCamelCasedKeys } from '@agilepoint/utils-common';
import { StyledPrimaryButton, StyledStackPanel } from 'common';
import { ReactComponent as DownloadBuildIllustration } from '../../../../assets/svg/download-build.svg';

const StyledBuildIllustrations = styled(DownloadBuildIllustration)`
    width: 100%;
    height: 80%;
    ${StyledStackPanel}{
        box-sizing: border-box;
    }
`;

const ContentWrapper = styled(StyledStackPanel)`
    box-sizing: border-box;
`;

const StyledProgressIndicator = styled(ProgressIndicator)`
    width: 100%;
`;

export const DownloadBuildPage = () => {
    const { refetch } = useGetBuildRequestByStatusOrBuildIdQuery();
    const [downloadPackage] = useOptionalDownloadMobilePackageQuery();
    const [progress, setProgress] = React.useState<number | undefined>(undefined);

    const onDownload = React.useCallback(async () => {
        const { data } = await refetch();
        if (data) {
            const res = toCamelCasedKeys(data[0]) as IMobileBuild;

            try {
                setProgress(0);
                const packageResponse = await downloadPackage(res.buildRequestId);
                const reader = packageResponse?.body?.getReader();
                const contentLength = Number(packageResponse?.headers.get('Content-Length'));
                let receivedLength = 0; // received 'n' number of bytes at the moment
                const chunks = []; // array of received binary chunks (comprises the body)

                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const { done, value } = await reader!.read();
                    if (done) {
                        break;
                    }
                    chunks.push(value);
                    receivedLength += value.length;
                    const currentProgress = Math.round((receivedLength / contentLength) * 100);
                    setProgress(currentProgress);
                }

                const blob = new Blob(chunks, {
                    type: 'application/zip'
                });
                const fileName = `${res?.appFileDisplayName}_${res?.appType}.zip`;
                saveFile(blob, fileName, 'application/zip');

            } catch (error) {
                setProgress(0);
            }
        }
    }, [downloadPackage, refetch]);

    const downloadBuildButtonLabel = () => (
        progress !== undefined ? 'Downloading Build...' : 'Download Build'
    );

    return (
        <ContentWrapper width="100%" height="100%" orientation='vertical' alignment="center" padding='20px' gap="40">
            <StyledBuildIllustrations />
            {progress === 100
                ? <Typography.H3>Thank you for downloading the app package</Typography.H3>
                : <StyledPrimaryButton label={downloadBuildButtonLabel()} onClick={onDownload} disabled={progress !== undefined} />}
            <StyledProgressIndicator progress={progress} hidden={progress === undefined || progress === 100} progressIndicatorType="determinate" showPercentage />
        </ContentWrapper>
    );
};
